import { insertStateEntity, deleteStateEntity } from './Assets.duck';
import { addMarketplaceEntities, getListingsById } from './marketplaceData.duck';

import { types as sdkTypes } from '../util/sdkLoader';
const { UUID } = sdkTypes;

export const RIDER_LISTINGS_LOADING_REQUEST_INITIATED =
    'app/riderListings/RIDER_LISTINGS_LOADING_REQUEST_INITIATED';
export const RIDER_LISTINGS_LOADING_REQUEST_FINISHED =
    'app/riderListings/RIDER_LISTINGS_LOADING_REQUEST_FINISHED';
export const RIDER_LISTINGS_LOADING_SUCCESS = 'app/riderListings/RIDER_LISTINGS_LOADING_SUCCESS';

/**
 * A non-empty result never goes stale here - a rider listing, once created,
 * is not expected to disappear. An empty result is different: it means "no
 * listing yet", which can become true at any moment, so it is only trusted
 * for a short while before the next mount is allowed to check again.
 */
export const RIDER_LISTINGS_EMPTY_RESULT_TTL = 5 * 60 * 1000;

const initialState = {
    riderListings: {}, // { [authorId]: [listingId,listingId...] }
    riderListingsFetchedAt: {}, // { [authorId]: timestamp }
    riderListingsRequests: {}, // { [authorId]: authorId }
};

export default function reducer(state = initialState, action = {}) {
    const { type, payload } = action;
    const { listingIds, authorId } = payload || {};

    switch (type) {
        case RIDER_LISTINGS_LOADING_REQUEST_INITIATED:
            return {
                ...state,
                riderListingsRequests: insertStateEntity(
                    state,
                    'riderListingsRequests',
                    authorId,
                    authorId
                ),
            };
        case RIDER_LISTINGS_LOADING_REQUEST_FINISHED:
            return {
                ...state,
                riderListingsRequests: deleteStateEntity(state, 'riderListingsRequests', authorId),
            };
        case RIDER_LISTINGS_LOADING_SUCCESS:
            return {
                ...state,
                riderListings: insertStateEntity(
                    state,
                    'riderListings',
                    authorId,
                    listingIds.map(({ id: { uuid } }) => new UUID(uuid))
                ),
                riderListingsFetchedAt: insertStateEntity(
                    state,
                    'riderListingsFetchedAt',
                    authorId,
                    payload.fetchedAt
                ),
            };
        default:
            return state;
    }
}

const addSdkListingRequest = authorId => ({
    type: RIDER_LISTINGS_LOADING_REQUEST_INITIATED,
    payload: {
        authorId,
    },
});

const addSdkListingRequestFinished = authorId => ({
    type: RIDER_LISTINGS_LOADING_REQUEST_FINISHED,
    payload: {
        authorId,
    },
});

const addSdkListingData = ({ listingIds, authorId, fetchedAt }) => ({
    type: RIDER_LISTINGS_LOADING_SUCCESS,
    payload: { listingIds, authorId, fetchedAt },
});

/**
 * One-entry cache for `selectOwnRiderListings`. It is read through `useSelector`,
 * which re-renders on any reference change, and both the `[listings, loading]`
 * tuple and `getListingsById`'s denormalised output are new on every call. Without
 * this, every redux action re-renders the caller, and SearchPage dispatches
 * `setMapBounds` on every render - an infinite loop.
 */
let lastInputs = null;
let lastResult = null;

/**
 * The current user's own listings, as already fetched (once, session-wide) by
 * `queryRiderListing` via `middlewares/currentUser.js`. Callers that used to fire
 * their own `useListings({ authorId })` for the same data should read this instead -
 * it never triggers a fetch, it only reports what is already in the store.
 *
 * @returns {[Array|null, boolean]} `[listings, loading]` - `null` means not fetched
 * yet, `[]` means fetched and empty (see RIDER_LISTINGS_EMPTY_RESULT_TTL above).
 */
export const selectOwnRiderListings = (state, authorId) => {
    const { riderListings, riderListingsRequests } = state.riderListings;
    const ids = riderListings && riderListings[authorId];
    const loading = Boolean(riderListingsRequests && riderListingsRequests[authorId]);
    const { entities } = state.marketplaceData;

    if (
        lastInputs &&
        lastInputs.entities === entities &&
        lastInputs.ids === ids &&
        lastInputs.loading === loading
    ) {
        return lastResult;
    }

    const listings = Array.isArray(ids) ? getListingsById(state, ids) : null;

    lastInputs = { entities, ids, loading };
    lastResult = [listings, loading];

    return lastResult;
};

/**
 * @param {string} authorId
 * @param {{ force?: boolean }} [options] - `force` skips the stored-result check below,
 * for callers that know the listing changed (e.g. a rider re-confirmed availability
 * and the backend re-opened their listing). A request already in flight is still reused.
 */
export const queryRiderListing = (authorId, { force = false } = {}) => async (
    dispatch,
    getState,
    sdk
) => {
    const {
        riderListings: { riderListings, riderListingsFetchedAt, riderListingsRequests },
    } = getState();

    const requestInProgress = riderListingsRequests && riderListingsRequests[authorId];
    /**
     * do not re-request the current user's listings once they are in the store.
     *
     * A non-empty result is trusted indefinitely. An empty one is trusted only
     * for RIDER_LISTINGS_EMPTY_RESULT_TTL, so a listing published after the
     * first (empty) answer is picked up on the next call past that window.
     */
    const listingIds = riderListings && riderListings[authorId];
    const hasListings = Array.isArray(listingIds) && listingIds.length > 0;

    const fetchedAt = riderListingsFetchedAt && riderListingsFetchedAt[authorId];
    const emptyResultIsFresh =
        Array.isArray(listingIds) &&
        listingIds.length === 0 &&
        typeof fetchedAt === 'number' &&
        Date.now() - fetchedAt < RIDER_LISTINGS_EMPTY_RESULT_TTL;

    const alreadyFetched = hasListings || emptyResultIsFresh;

    if (requestInProgress || (alreadyFetched && !force)) {
        return;
    }

    try {
        dispatch(addSdkListingRequest(authorId));

        const response = await sdk.listings.query({
            authorId,
        });
        const {
            data: { data },
        } = response;

        if (data.length) {
            dispatch(addMarketplaceEntities(response));
        }

        dispatch(addSdkListingData({ listingIds: data, authorId, fetchedAt: Date.now() }));

        return response;
    } catch (e) {
        // do nothing
    } finally {
        dispatch(addSdkListingRequestFinished(authorId));
    }
};
